import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, IconButton, Button, Card, CardContent, Snackbar, Tooltip,
  Modal, MenuItem, Select, FormControl, InputLabel, TextField, Switch
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import Alert from '@mui/material/Alert';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PreviewIcon from '@mui/icons-material/Preview'; // 👁️ PDF önizleme için

export default function AdminEducationPage() {
  const [educationList, setEducationList] = useState([]);
  const [pdfList, setPdfList] = useState([]);
  const [modalUploadSuccess, setModalUploadSuccess] = useState(false);
  const [editContent, setEditContent] = useState(undefined); // Yeni/Düzenleme modalı
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedPdfList, setSelectedPdfList] = useState([]);
  const [pdfListModalOpen, setPdfListModalOpen] = useState(false);
  const [showPdfUploadModal, setShowPdfUploadModal] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const categories = [...new Set(educationList.map(e => e.category).filter(Boolean))];
  const isEditModalOpen = editContent !== undefined;
  const [selectedPdfContentId, setSelectedPdfContentId] = useState('');
  const [selectedPdfListTitle, setSelectedPdfListTitle] = useState('');
  const [selectedPdfIds, setSelectedPdfIds] = useState([]);
  const [modalUploadFiles, setModalUploadFiles] = useState([]);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [showUnlinkedOnly, setShowUnlinkedOnly] = useState(true);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [warningMessage, setWarningMessage] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    isActive: true
  });
  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: true,
    onDrop: (acceptedFiles) => {
      setSelectedFiles(prev => [...prev, ...acceptedFiles]);
    }
  });
  useEffect(() => {
    refreshEducationList();
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/pdf`)
      .then(res => setPdfList(res.data));
  }, []);
  const {
    getRootProps: getModalDropzoneProps,
    getInputProps: getModalInputProps
  } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true,
    onDrop: (files) => setModalUploadFiles(files)
  });



  const filteredList = useMemo(() => {
    return educationList.filter(item => {
      const matchCategory = categoryFilter ? item.category === categoryFilter : true;
      const matchStatus =
        statusFilter === ''
          ? true
          : item.isActive === (statusFilter === 'true');
      return matchCategory && matchStatus;
    });
  }, [educationList, categoryFilter, statusFilter]);


  const refreshEducationList = () => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/education`)
      .then(res => setEducationList(res.data));
  };
  const handleDeletePdf = async (pdfId) => {
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/pdf/${pdfId}`);
      setPdfList(prev => prev.filter(p => p._id !== pdfId));
      setDeleteSuccess(true);
    } catch (err) {
      const msg = err.response?.data?.message || 'PDF silinemedi.';
      setDeleteError(msg);
    }
  };

  const handleRemovePdf = async (contentId, pdfId) => {
    await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/education/${contentId}/remove-pdf`, { pdfId });
    setSelectedPdfList(prev => prev.filter(pdf => pdf._id !== pdfId));
    refreshEducationList();
  };
  const handleDeleteContent = async (id) => {
    if (!window.confirm('Bu eğitim içeriğini silmek istediğinizden emin misiniz?')) return;

    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/education/${id}`);
      refreshEducationList();
    } catch (err) {
      console.error('Silme hatası:', err);
    }
  };
  const handleAddMultiplePdfs = async () => {
    const existingPdfIds = selectedPdfList.map(pdf => pdf._id);
    const duplicates = selectedPdfIds.filter(id => existingPdfIds.includes(id));
    const newPdfIds = selectedPdfIds.filter(id => !existingPdfIds.includes(id));

    if (newPdfIds.length === 0) {
      setWarningMessage('Seçilen tüm PDF’ler zaten eklenmiş.');
      return;
    }

    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/education/${selectedPdfContentId}/add-multiple-pdfs`, {
        pdfIds: newPdfIds
      });
      refreshEducationList();
      // 🔁 Listeyi frontend tarafında da güncelle
      const newlyAddedPdfs = pdfList.filter(p => newPdfIds.includes(p._id));
      setSelectedPdfList(prev => [...prev, ...newlyAddedPdfs]);
      setSelectedPdfIds([]);
      if (duplicates.length > 0) {
        setWarningMessage(`${duplicates.length} PDF zaten bu içeriğe eklenmişti.`);
      }
    } catch (err) {
      console.error('PDF ekleme hatası:', err);
    }
  };


  const handleUploadFiles = async () => {
    const formData = new FormData();
    selectedFiles.forEach(file => formData.append('pdfs', file));

    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/pdf/upload-multiple`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setPdfList(prev => [...prev, ...res.data]);
      if (selectedPdfContentId && pdfListModalOpen) {
        setSelectedPdfList(prev => [...prev, ...res.data]);
      }
      setSelectedFiles([]); // input sıfırlansın
      setShowPdfUploadModal(false);
      setUploadSuccess(true);
    } catch (err) {
      console.error('PDF yükleme hatası:', err);
    }
  };

  const openEditModal = (content = null) => {
    if (content) {
      setFormData({
        title: content.title,
        description: content.description,
        category: content.category,
        isActive: content.isActive
      });
    } else {
      setFormData({
        title: '',
        description: '',
        category: '',
        isActive: true
      });
    }
    setEditContent(content);
  };

  const handleSaveContent = async () => {
    if (editContent) {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/education/${editContent._id}`, {
        ...formData,
        pdfs: editContent.pdfs.map(p => (typeof p === 'string' ? p : p._id))
      });
    } else {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/education`, {
        ...formData,
        pdfs: []
      });
    }
    refreshEducationList();
    setEditContent(undefined);
  };

  const handleModalPdfUpload = async () => {
    const formData = new FormData();
    modalUploadFiles.forEach(file => formData.append('pdfs', file));

    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/pdf/upload-multiple`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const uploadedPdfs = res.data; // her biri { _id, filename, url }

      // 1. pdfList ve selectedPdfList'e ekle
      setPdfList(prev => [...prev, ...uploadedPdfs]);
      setSelectedPdfList(prev => [...prev, ...uploadedPdfs]);
      setModalUploadFiles([]);


      for (const pdf of uploadedPdfs) {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/education/${selectedPdfContentId}/add-pdf`, {
          pdfId: pdf._id
        });
      }
      await refreshEducationList();

      setModalUploadSuccess(true);
      setUploadSuccess(true);

    } catch (err) {
      console.error('Yükleme hatası:', err);
    }
  };

  const columns = [
    { field: 'title', headerName: 'Başlık', flex: 1 },
    { field: 'category', headerName: 'Kategori', flex: 0.8 },
    {
      field: 'isActive',
      headerName: 'Durum',
      flex: 0.5,
      renderCell: (params) => (
        <Typography variant="caption" color={params.value ? 'green' : 'gray'}>
          {params.value ? 'Aktif' : 'Pasif'}
        </Typography>
      )
    },
    {
      field: 'pdfs',
      headerName: 'PDF Dosyaları',
      flex: 0.8,
      renderCell: (params) => {
        const pdfs = params.row.pdfs || [];
        return (
          <Tooltip title={pdfs.map(p => p.filename).join(', ') || 'PDF yok'}>
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%',             // Hücre yüksekliğini kullan
              width: '100%', gap: 1
            }}>
              <InsertDriveFileIcon fontSize="small" />
              <Typography variant="body2">{pdfs.length}</Typography>
            </Box>
          </Tooltip>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'İşlemler',
      flex: 1,
      renderCell: (params) => (
        <Box>
          <IconButton onClick={() => openEditModal(params.row)} title="Düzenle">
            <EditIcon />
          </IconButton>
          <IconButton onClick={() => handleDeleteContent(params.row._id)} title="Sil">
            <DeleteIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              setSelectedPdfList(params.row.pdfs || []);
              setSelectedPdfContentId(params.row._id);
              setSelectedPdfListTitle(params.row.title);
              setPdfListModalOpen(true);
            }}
            title="PDF'leri Gör"
          >
            <PreviewIcon />
          </IconButton>

        </Box>
      )
    }
  ];

  return (

    <Box sx={{ p: 3 }}>
      <Snackbar
        open={uploadSuccess}
        autoHideDuration={10000}
        onClose={() => setUploadSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert onClose={() => setUploadSuccess(false)} severity="success" sx={{ width: '100%' }}>
          PDF dosyaları başarıyla yüklendi!
        </Alert>
      </Snackbar>
      {/*Kartlar */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap', justifyContent: 'center' }}>


        <Card sx={{ width: 200, textAlign: 'center', boxShadow: 3 }}>
          <CardContent>
            <LibraryBooksIcon sx={{ fontSize: 40, color: '#4caf50' }} />
            <Typography variant="subtitle2">Aktif Eğitim</Typography>
            <Typography variant="h5">
              {educationList.filter(e => e.isActive).length}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ width: 200, textAlign: 'center', boxShadow: 3 }}>
          <CardContent>
            <LibraryBooksIcon sx={{ fontSize: 40, color: '#1976d2' }} />
            <Typography variant="subtitle2">Toplam Eğitim</Typography>
            <Typography variant="h5">{educationList.length}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ width: 200, textAlign: 'center', boxShadow: 3 }}>
          <CardContent>
            <PictureAsPdfIcon sx={{ fontSize: 40, color: '#f44336' }} />
            <Typography variant="subtitle2">Toplam PDF</Typography>
            <Typography variant="h5">{pdfList.length}</Typography>
          </CardContent>
        </Card>
      </Box>
      {/* Yeni içerik ekle butonu */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button variant="contained" onClick={() => openEditModal(null)} sx={{ mb: 2, mt: 4 }}>
          Yeni Eğitim İçeriği Ekle
        </Button>
        <Button color="inherit" startIcon={<PictureAsPdfIcon />} variant="contained" sx={{ ml: 2, mb: 2, mt: 4 }} onClick={() => setShowPdfUploadModal(true)}>
          Yeni PDF Ekle
        </Button>
        <Button
          variant="outlined"
          startIcon={<InsertDriveFileIcon />}
          onClick={() => setShowPdfModal(true)}
          sx={{ ml: 1, mt: 4, mb: 2 }}
        >
          PDF'leri Göster
        </Button>
      </Box>
      {/* Eğitim listesi */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl sx={{ minWidth: 200, mt: 4 }}>
          <InputLabel shrink id="category-filter-label">Kategori</InputLabel>
          <Select
            labelId="category-filter-label"
            value={categoryFilter}
            sx={{ mt: 2 }}
            onChange={(e) => setCategoryFilter(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">Tümü</MenuItem>
            {categories.map((cat, i) => (
              <MenuItem key={i} value={cat}>{cat}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200, mt: 4 }}>
          <InputLabel shrink id="status-filter-label">Durum</InputLabel>
          <Select
            labelId="status-filter-label"
            value={statusFilter}
            sx={{ mt: 2 }}
            onChange={(e) => setStatusFilter(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">Hepsi</MenuItem>
            <MenuItem value="true">Aktif</MenuItem>
            <MenuItem value="false">Pasif</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ width: '97%', overflowX: 'auto', mb: 2 }}>
        <div style={{ minWidth: 1000, height: 550 }}>
          <DataGrid
            rows={filteredList.map(e => ({ id: e._id, ...e }))}
            overflow="visible"
            columns={columns}
            pageSize={10}
          />
        </div>
      </Box>

      {/* Eğitim içeriği ekle/düzenle modalı */}
      <Modal open={isEditModalOpen} onClose={() => setEditContent(undefined)}>
        <Box sx={{ p: 4, bgcolor: 'white', mx: 'auto', my: '5%', width: 500, borderRadius: 2, position: 'relative' }}>
          <IconButton
            onClick={() => setEditContent(undefined)}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="h6" gutterBottom>
            {editContent ? 'Eğitim İçeriğini Düzenle' : 'Yeni Eğitim İçeriği'}
          </Typography>

          <TextField
            label="Başlık"
            fullWidth
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Açıklama"
            fullWidth
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Kategori"
            fullWidth
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            sx={{ mb: 2 }}
          />

          <Box display="flex" alignItems="center" gap={2}>
            <Typography>Aktif mi?</Typography>
            <Switch
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
            />
          </Box>

          <Box mt={2}>
            <Button variant="contained" onClick={handleSaveContent}>
              Kaydet
            </Button>
          </Box>
        </Box>
      </Modal>
      <Modal open={showPdfUploadModal} onClose={() => setShowPdfUploadModal(false)}>
        <Box sx={{
          p: 4,
          bgcolor: 'white',
          mx: 'auto',
          my: '10%',
          width: 500,
          borderRadius: 2,
          position: 'relative'
        }}>
          <IconButton
            sx={{ position: 'absolute', top: 8, right: 8 }}
            onClick={() => setShowPdfUploadModal(false)}
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="h6" gutterBottom>
            PDF Yükle
          </Typography>

          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed #aaa',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: '#fafafa',
              mb: 2
            }}
          >
            <input {...getInputProps()} />
            <Typography sx={{ height: 100, justifyContent: 'center', display: 'flex', alignItems: 'center' }}>
              PDF dosyalarını buraya bırakabilirsiniz veya tıklayarak seçininiz
            </Typography>
          </Box>

          {selectedFiles.length > 0 && (
            <Box>
              <Typography variant="subtitle2" mb={1}>Seçilen Dosyalar:</Typography>
              {selectedFiles.map((file, i) => (
                <Typography key={i} variant="body2">• {file.name}</Typography>
              ))}

              <Button
                variant="contained"
                fullWidth
                sx={{ mt: 2 }}
                onClick={handleUploadFiles}
              >
                Yükle ({selectedFiles.length})
              </Button>
            </Box>
          )}
        </Box>
      </Modal>
      {/* PDF Listesi Modal */}
      {pdfListModalOpen && selectedPdfContentId && (
        <Modal open={pdfListModalOpen} onClose={() => setPdfListModalOpen(false)}>
          <Box sx={{
            p: 4,
            bgcolor: 'white',
            mx: 'auto',
            my: '10%',
            width: 500,
            borderRadius: 2,
            maxHeight: 600,
            overflowY: 'auto',
            position: 'relative'
          }}>
            {/* Kapat butonu */}
            <IconButton
              sx={{ position: 'absolute', top: 8, right: 8 }}
              onClick={() => setPdfListModalOpen(false)}
            >
              <CloseIcon />
            </IconButton>

            <Typography variant="h6" sx={{ mb: 2 }} gutterBottom>
              PDF Dosyaları – {selectedPdfListTitle}
            </Typography>

            {/* PDF listesi */}
            {selectedPdfList.length === 0 ? (
              <Typography>Bu içerikte PDF bulunmuyor.</Typography>
            ) : (
              selectedPdfList.map((pdf) => (
                <Box
                  key={pdf._id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <a
                      href={`${pdf.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <InsertDriveFileIcon />
                    </a>
                    <Typography variant="body2">{pdf.filename}</Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleRemovePdf(selectedPdfContentId, pdf._id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))
            )}

            {/* Yeni PDF seçimi */}
            <Box mt={3}>
              <FormControl fullWidth>
                <InputLabel>Yeni PDF Seç</InputLabel>
                <Select
                  multiple
                  value={selectedPdfIds} // array!
                  onChange={(e) => setSelectedPdfIds(e.target.value)}
                  renderValue={(selected) =>
                    selected.map(id => {
                      const found = pdfList.find(p => p._id === id);
                      return found ? found.filename : '';
                    }).join(', ')
                  }
                >
                  {pdfList.map(pdf => (
                    <MenuItem key={pdf._id} value={pdf._id}>
                      {pdf.filename}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                sx={{ mt: 2 }}
                onClick={handleAddMultiplePdfs}
                disabled={selectedPdfIds.length === 0}
              >
                Ekle
              </Button>
            </Box>

            {/* Dropzone – Yeni PDF yükle */}
            <Box
              {...getModalDropzoneProps()}
              sx={{
                border: '2px dashed #aaa',
                borderRadius: 2,
                height: 100,
                p: 2,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: '#f9f9f9',
                mt: 4
              }}
            >
              <input {...getModalInputProps()} />
              <Typography variant="body2" sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                Yeni PDF yüklemek için buraya sürükleyin ya da tıklayın
              </Typography>
            </Box>

            {modalUploadFiles.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>Seçilen Dosyalar:</Typography>
                {modalUploadFiles.map((file, i) => (
                  <Typography key={i} variant="body2">• {file.name}</Typography>
                ))}
                <Button
                  variant="contained"
                  fullWidth
                  sx={{ mt: 1 }}
                  onClick={handleModalPdfUpload}
                >
                  Seçilen PDF'leri Yükle
                </Button>
              </Box>
            )}
          </Box>
        </Modal>)}
      <Snackbar
        open={modalUploadSuccess}
        autoHideDuration={6000}
        onClose={() => setModalUploadSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setModalUploadSuccess(false)} severity="success" sx={{ width: '100%' }}>
          PDF yüklendi ve eğitim içeriğine başarıyla eklendi!
        </Alert>
      </Snackbar>
      <Modal open={showPdfModal} onClose={() => setShowPdfModal(false)}>
        <Box sx={{
          p: 4,
          bgcolor: 'white',
          mx: 'auto',
          my: '5%',
          width: 600,
          borderRadius: 2,
          maxHeight: 600,
          overflowY: 'auto',
          position: 'relative'
        }}>
          <IconButton
            sx={{ position: 'absolute', top: 8, right: 8 }}
            onClick={() => setShowPdfModal(false)}
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="h6" gutterBottom>PDF Listesi</Typography>

          {/* Filtre */}
          <Box sx={{ mb: 2 }}>
            <label>
              <input
                type="checkbox"
                checked={showUnlinkedOnly}
                onChange={(e) => setShowUnlinkedOnly(e.target.checked)}
              />{' '}
              Sadece kullanılmayan PDF'leri göster
            </label>
          </Box>

          {/* Liste */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {pdfList
              .filter(pdf => {
                if (!showUnlinkedOnly) return true;
                // İçeriklerde geçmiyorsa ilişkisizdir
                const isLinked = educationList.some(e => e.pdfs?.some(p => (typeof p === 'string' ? p === pdf._id : p._id === pdf._id)));
                return !isLinked;
              })
              .map(pdf => (
                <Box
                  key={pdf._id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    border: '1px solid #eee',
                    borderRadius: 1,
                    px: 2,
                    py: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <a
                      href={`${pdf.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <InsertDriveFileIcon />
                    </a>
                    <Typography variant="body2">{pdf.filename}</Typography>
                  </Box>
                  {educationList.some(e => e.pdfs?.some(p => (typeof p === 'string' ? p === pdf._id : p._id === pdf._id))) ? (
                    <Typography variant="caption" color="gray">Bağlı</Typography>
                  ) : (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => handleDeletePdf(pdf._id)}
                    >
                      Sil
                    </Button>
                  )}
                </Box>
              ))}
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={deleteSuccess}
        autoHideDuration={3000}
        onClose={() => setDeleteSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setDeleteSuccess(false)} severity="success" sx={{ width: '100%' }}>
          PDF başarıyla silindi!
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!deleteError}
        autoHideDuration={4000}
        onClose={() => setDeleteError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setDeleteError('')} severity="error" sx={{ width: '100%' }}>
          {deleteError}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!warningMessage}
        autoHideDuration={5000}
        onClose={() => setWarningMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setWarningMessage('')}
          severity="warning"
          sx={{ width: '100%' }}
        >
          {warningMessage}
        </Alert>
      </Snackbar>

    </Box>

  );
}
